import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { filter, map, startWith } from "rxjs";
import { MobileSessionService } from "../../auth/mobile-session.service";
import { MobileAuthStore } from "../../state/mobile-auth.store";
import { MobileNetworkService } from "../../platform/mobile-network.service";
import { BottomNavComponent } from "../bottom-nav/bottom-nav.component";
import { MobileQuickAccessFabComponent } from "../mobile-quick-access-fab/mobile-quick-access-fab.component";
import { MobileChatComposerNavService } from "../mobile-chat-composer-nav.service";
import { MobileAppContextService } from "../mobile-app-context.service";
import { MobileBurgerDrawerComponent } from "../mobile-burger-drawer/mobile-burger-drawer.component";
import { MobileHeaderComponent } from "../mobile-header/mobile-header.component";
import { MOBILE_BASE_PATH, mobilePath } from "../mobile-routes.constants";

const LAUNCHER_URL_FRAGMENT = `${MOBILE_BASE_PATH}/launcher`;
const LOGIN_URL_FRAGMENT = `${MOBILE_BASE_PATH}/auth/login`;

@Component({
  selector: "mobile-shell",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    TranslateModule,
    MobileHeaderComponent,
    BottomNavComponent,
    MobileBurgerDrawerComponent,
    MobileQuickAccessFabComponent,
  ],
  templateUrl: "./mobile-shell.component.html",
  styleUrl: "./mobile-shell.component.scss",
})
export class MobileShellComponent {
  readonly authStore = inject(MobileAuthStore);
  readonly networkService = inject(MobileNetworkService);
  readonly appContext = inject(MobileAppContextService);
  private readonly sessionService = inject(MobileSessionService);
  private readonly router = inject(Router);

  readonly burgerOpen = signal(false);

  readonly headerTitleKey = computed(() => "app.launcher.title");

  readonly headerTitleText = computed(() => this.appContext.activeApplication()?.Name ?? null);

  readonly bottomNavItems = this.appContext.bottomNavItems;

  readonly quickAccessItems = this.appContext.quickAccessItems;

  readonly isOnLauncher = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.urlMatchesLauncher(event.urlAfterRedirects)),
      startWith(this.urlMatchesLauncher(this.router.url))
    ),
    { initialValue: this.urlMatchesLauncher(this.router.url) }
  );

  readonly showShellChrome = computed(() => !this.isOnLauncher());

  readonly showBottomNav = computed(
    () => !this.isOnLauncher() && !!this.appContext.activeApplication()
  );

  private readonly composerNav = inject(MobileChatComposerNavService);

  /**
   * Barre du bas : masquée sur le chat tant que le composeur a le focus (plus d’espace pour l’historique).
   */
  readonly showBottomNavDock = computed(
    () =>
      this.showBottomNav() &&
      !(
        this.fixedBottomComposerLayout() && this.composerNav.hideBottomNavForComposerFocus()
      )
  );

  readonly composerFocusNoDock = computed(
    () =>
      this.fixedBottomComposerLayout() && this.composerNav.hideBottomNavForComposerFocus()
  );

  /**
   * Écrans dont la zone de saisie est fixée en bas (chat) : le grand padding du scroll host
   * réserve un vide inutile sous le composeur ; on le retire et on réserve la hauteur dans le panneau.
   */
  readonly fixedBottomComposerLayout = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.urlUsesFixedBottomComposer(event.urlAfterRedirects)),
      startWith(this.urlUsesFixedBottomComposer(this.router.url)),
    ),
    { initialValue: this.urlUsesFixedBottomComposer(this.router.url) },
  );

  private urlMatchesLauncher(url: string): boolean {
    const path = (url.split("?")[0] ?? "").replace(/\/+$/, "").toLowerCase();
    return path === LAUNCHER_URL_FRAGMENT.toLowerCase();
  }

  constructor() {
    effect(() => {
      if (!this.fixedBottomComposerLayout()) {
        this.composerNav.setComposerFocused(false);
      }
    });

    effect(() => {
      if (this.authStore.workflowState() !== "unauthenticated") {
        return;
      }

      if (this.isOnLoginPage(this.router.url)) {
        return;
      }

      void this.router.navigateByUrl(LOGIN_URL_FRAGMENT);
    });
  }

  onLogout(): void {
    this.closeBurger();
    this.sessionService.logout().catch(() => undefined);
  }

  private isOnLoginPage(url: string): boolean {
    const path = (url.split("?")[0] ?? "").replace(/\/+$/, "").toLowerCase();
    return path === LOGIN_URL_FRAGMENT.toLowerCase();
  }

  private urlUsesFixedBottomComposer(url: string): boolean {
    const path = (url.split("?")[0] ?? "").replace(/\/+$/, "");
    return /\/t\/[^/]+\/chat$/i.test(path) || /\/echantier\/chat$/i.test(path);
  }

  toggleBurger(): void {
    this.burgerOpen.update((state) => !state);
  }

  closeBurger(): void {
    this.burgerOpen.set(false);
  }

  onMenuSelected(menuUid: string): void {
    const target = this.appContext.resolveMenuTarget(menuUid);
    if (!target) {
      return;
    }

    this.closeBurger();
    void this.router.navigateByUrl(target.Url);
  }

  async onBackToApps(): Promise<void> {
    this.closeBurger();
    await this.appContext.clearActiveApplication();
    void this.router.navigateByUrl(mobilePath("launcher"));
  }

  onItemSelected(route: string): void {
    this.closeBurger();
    void this.router.navigateByUrl(route);
  }
}
